import React, { useMemo } from 'react';
import useSoWhatNarratives from '../../hooks/useSoWhatNarratives';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * Build a lookup key from location IDs and optional month
 */
function buildKey(locationIds, monthKey = null) {
  const sorted = [...locationIds].sort();
  if (monthKey) {
    return `${sorted.join('|')}|${monthKey}`;
  }
  return sorted.join('|');
}

/**
 * SoWhatPanel displays AI-generated narrative insights about weather conditions
 * for the selected locations and time period.
 */
export default function SoWhatPanel({ locationIds, mode, monthIndex, unitSystem }) {
  const { data, loading, error } = useSoWhatNarratives(unitSystem);

  const narrativeKey = useMemo(() => {
    if (!locationIds || locationIds.length === 0) return null;
    if (mode === 'trip') {
      return buildKey(locationIds, MONTH_KEYS[monthIndex]);
    }
    return buildKey(locationIds);
  }, [locationIds, mode, monthIndex]);

  const bullets = useMemo(() => {
    if (!data || !narrativeKey) return null;
    const section = mode === 'trip' ? data.trip : data.year;
    return section?.[narrativeKey] || null;
  }, [data, narrativeKey, mode]);

  // Don't render anything while loading or if no locations selected
  if (loading || !locationIds || locationIds.length === 0) {
    return null;
  }

  // Don't render if there's an error (fail silently)
  if (error) {
    console.warn('SoWhatPanel: Failed to load narratives', error);
    return null;
  }

  // Don't render if no narrative found for this key
  if (!bullets || bullets.length === 0) {
    console.warn(`SoWhatPanel: No narrative found for key "${narrativeKey}"`);
    return null;
  }

  return (
    <div className="border border-white/15 rounded-lg bg-white/5 backdrop-blur p-4 md:p-5">
      <ul className="space-y-1.5">
        {bullets.map((bullet, idx) => (
          <li key={idx} className="flex gap-2 text-white/85 text-[14px] leading-snug">
            <span className="text-emerald-300 mt-0.5 shrink-0">•</span>
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
